import React, {useState} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	Detail,
	DropdownHandler,
	FlexColumn,
	Icon,
	Label,
	Modal,
	Pill,
	PillVariant,
	Text,
} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import DeleteNovaAgentVersionMutation from '../../../../mutations/nova/DeleteNovaAgentVersionMutation';
import {toRelayGlobalId} from '../shared/relayGlobalId';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaVisuallyHidden} from '../shared/novaVisuallyHidden';

export interface NovaWorkflowVersionEntry {
	versionNumber: number;
	versionName: string | null;
	createdAt: string | null;
	createdByName: string | null;
	updatedAt: string | null;
	updatedByName: string | null;
	isCurrent: boolean;
	// How many companies run this version, or null when the deployment could not be read, so the list
	// never claims a version is not deployed without knowing.
	companyCount: number | null;
}

export interface NovaWorkflowVersionHistoryModalProps {
	agentId: string;
	versions: NovaWorkflowVersionEntry[];
	// The version open in the editor: its Edit is disabled, and it carries the Editing chip unless it is
	// the latest.
	openVersionNumber: number;
	// Loads the version into the editor. The modal closes first.
	onEdit: (versionNumber: number) => void;
	// Asks for the version's new name. The modal closes first.
	onRename: (versionNumber: number) => void;
	// Opens the Deploy tab with the companies on this version highlighted. The modal closes first.
	onShowDeployed: (versionNumber: number) => void;
	// Asked before a delete is sent; false keeps the version. Lets the caller confirm away unsaved edits
	// when the version open in the editor is the one going.
	onBeforeDelete?: (versionNumber: number) => boolean;
	// Fired after a version is deleted, so the caller can bring its version list forward.
	onDeleted: (versionNumber: number) => void;
	onClose: () => void;
}

const COLUMNS = 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 120px';

const HeaderRow = styled.div`
	display: grid;
	grid-template-columns: ${COLUMNS};
	align-items: center;
	column-gap: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid ${NovaStudioColor.border};
`;
const VersionRow = styled(HeaderRow)`
	min-height: 56px;
	padding: 8px 0;
`;
const Cell = styled.div`
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
`;
const StackedCell = styled(Cell)`
	display: flex;
	flex-direction: column;
`;
// The count is a low button whose label is inset by its own padding; pulling the cell back by that
// padding lines its text up with the column heading, as "Not deployed" is.
const DeployedCell = styled.div`
	display: flex;
	min-width: 0;
	margin-left: -12px;
`;
const NotDeployed = styled.div`
	padding-left: 12px;
	white-space: nowrap;
`;
const RowActions = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
`;
// Icon then label on one line. A row without an icon keeps the icon's width, so every label in the
// menu starts at the same place.
const MenuRow = styled.span`
	display: flex;
	align-items: center;
	gap: 10px;
	white-space: nowrap;
`;
const IconSpace = styled.span`
	display: flex;
	flex: none;
	width: 16px;
`;

// The design system's dropdown menu sits at z-index 49 and its modal at 1000, so a row's menu would
// open behind this modal. Radix copies the menu's z-index onto the wrapper it positions, so the wrapper
// is raised above the modal for as long as this modal is open.
const MenuAboveModalStyle = createGlobalStyle`
	[data-radix-popper-content-wrapper] {
		z-index: 1001 !important;
	}
`;

const SHORT_DATE: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'short', day: 'numeric'};
const DATE_TIME: Intl.DateTimeFormatOptions = {...SHORT_DATE, hour: '2-digit', minute: '2-digit'};

// The workflow's versions, newest first: each with its name, when it was created and by whom, its last
// save, and how many companies run it. Each row's menu edits, renames or deletes that version. The
// latest version and any version a company runs cannot be deleted, and the menu says why.
export function NovaWorkflowVersionHistoryModal({
	agentId,
	versions,
	openVersionNumber,
	onEdit,
	onRename,
	onShowDeployed,
	onBeforeDelete = () => true,
	onDeleted,
	onClose,
}: NovaWorkflowVersionHistoryModalProps): JSX.Element {
	const {formatMessage, formatDate} = useIntl();
	const [rows, setRows] = useState(versions);
	const [deletingVersion, setDeletingVersion] = useState<number | null>(null);
	const [failedVersion, setFailedVersion] = useState<NovaWorkflowVersionEntry | null>(null);

	const sortedRows = [...rows].sort((first, second) => second.versionNumber - first.versionNumber);
	const nameOf = (row: NovaWorkflowVersionEntry): string =>
		row.versionName || formatMessage({id: 'nova.versionHistory.untitled'});
	const shortDate = (value: string | null): string => (value ? formatDate(value, SHORT_DATE) : '—');
	// The full date and time, and who, on hover.
	const fullDate = (value: string | null, person: string | null): string | undefined =>
		value ? [formatDate(value, DATE_TIME), person].filter(Boolean).join(', ') : undefined;

	const closeThen = (action: (versionNumber: number) => void, versionNumber: number) => (): void => {
		onClose();
		action(versionNumber);
	};

	const handleDelete = (row: NovaWorkflowVersionEntry): void => {
		if (deletingVersion !== null || !onBeforeDelete(row.versionNumber)) {
			return;
		}
		setDeletingVersion(row.versionNumber);
		setFailedVersion(null);
		Util.CommitMutation(
			DeleteNovaAgentVersionMutation,
			{id: toRelayGlobalId('NovaAgent', agentId), versionNumber: row.versionNumber},
			() => {
				setDeletingVersion(null);
				setRows(current => current.filter(candidate => candidate.versionNumber !== row.versionNumber));
				onDeleted(row.versionNumber);
			},
			true,
			null,
			(transaction, onFailure) => {
				// A true return means a retry of this same mutation is already scheduled, so the delete is
				// still in flight and the row stays busy until that retry settles.
				if (onFailure(transaction) === true) {
					return;
				}
				setDeletingVersion(null);
				setFailedVersion(row);
			}
		);
	};

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={onClose}>
			<MenuAboveModalStyle />
			<Modal.Title>{formatMessage({id: 'nova.versionManager.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'xs'}>
					<HeaderRow>
						<Label>{formatMessage({id: 'nova.versionManager.columnName'})}</Label>
						<Label>{formatMessage({id: 'nova.versionManager.columnCreated'})}</Label>
						<Label>{formatMessage({id: 'nova.versionManager.columnUpdated'})}</Label>
						<Label>{formatMessage({id: 'nova.versionHistory.columnDeployedTo'})}</Label>
						<span />
					</HeaderRow>
					{sortedRows.map(row => {
						const isDeployed = row.companyCount !== null && row.companyCount > 0;
						const isOpen = row.versionNumber === openVersionNumber;
						// Only a version known to have companies on it is blocked. An unreadable count is no
						// reason to block a delete the service would allow.
						const canDelete = !row.isCurrent && !isDeployed && deletingVersion === null;
						const deleteLabelId = row.isCurrent
							? 'nova.versionManager.deleteLatest'
							: isDeployed
							? 'nova.versionManager.deleteInUse'
							: 'nova.versionHistory.deleteButton';
						const menuLabelId = `nova-version-menu-label-${row.versionNumber}`;
						return (
							<VersionRow key={row.versionNumber} data-cy={`nova-version-${row.versionNumber}`}>
								<Cell>
									<Text ellipsis color={row.versionName ? 'main' : 'auxiliary'}>
										{nameOf(row)}
									</Text>
								</Cell>
								<StackedCell
									title={fullDate(row.createdAt, row.createdByName)}
									data-cy={`nova-version-created-${row.versionNumber}`}
								>
									<Text>{shortDate(row.createdAt)}</Text>
									<Detail size="1" color="auxiliary">
										{row.createdByName ?? '—'}
									</Detail>
								</StackedCell>
								<StackedCell
									title={fullDate(row.updatedAt, row.updatedByName)}
									data-cy={`nova-version-updated-${row.versionNumber}`}
								>
									<Text>{shortDate(row.updatedAt)}</Text>
									<Detail size="1" color="auxiliary">
										{row.updatedByName ?? '—'}
									</Detail>
								</StackedCell>
								<DeployedCell data-cy={`nova-version-deployed-${row.versionNumber}`}>
									{isDeployed ? (
										<Button
											emphasis="low"
											data-cy={`nova-version-show-deployed-${row.versionNumber}`}
											title={formatMessage({id: 'nova.versionHistory.showDeployed'})}
											onPress={closeThen(onShowDeployed, row.versionNumber)}
										>
											{formatMessage(
												{id: 'nova.versionHistory.deployedCount'},
												{count: row.companyCount}
											)}
										</Button>
									) : (
										<NotDeployed>
											<Text color="auxiliary">
												{row.companyCount === null
													? '—'
													: formatMessage({id: 'nova.versionHistory.notDeployed'})}
											</Text>
										</NotDeployed>
									)}
								</DeployedCell>
								<RowActions>
									{isOpen && !row.isCurrent && (
										<span data-cy={`nova-version-editing-${row.versionNumber}`}>
											<Pill variant={PillVariant.INFORMATION}>
												{formatMessage({id: 'nova.versionManager.editing'})}
											</Pill>
										</span>
									)}
									<NovaVisuallyHidden id={menuLabelId}>
										{formatMessage({id: 'nova.versionManager.menuLabel'}, {versionName: nameOf(row)})}
									</NovaVisuallyHidden>
									<DropdownHandler>
										<DropdownHandler.Trigger>
											<Button
												emphasis="low"
												icon="more"
												aria-labelledby={menuLabelId}
												data-cy={`nova-version-menu-${row.versionNumber}`}
											/>
										</DropdownHandler.Trigger>
										<DropdownHandler.Content offset={6} align="end">
											<DropdownHandler.Item
												data-cy={`nova-version-edit-${row.versionNumber}`}
												disabled={isOpen}
												onClick={isOpen ? undefined : closeThen(onEdit, row.versionNumber)}
											>
												<MenuRow>
													<Icon icon="edit" size="s" color={isOpen ? 'disabled' : 'default'} />
													<Text color={isOpen ? 'disabled' : 'main'}>
														{formatMessage({id: 'nova.versionHistory.editButton'})}
													</Text>
												</MenuRow>
											</DropdownHandler.Item>
											<DropdownHandler.Item
												data-cy={`nova-version-rename-${row.versionNumber}`}
												onClick={closeThen(onRename, row.versionNumber)}
											>
												<MenuRow>
													<IconSpace />
													<Text>{formatMessage({id: 'nova.versionManager.rename'})}</Text>
												</MenuRow>
											</DropdownHandler.Item>
											<DropdownHandler.Separator />
											<DropdownHandler.Item
												data-cy={`nova-version-delete-${row.versionNumber}`}
												disabled={!canDelete}
												onClick={canDelete ? () => handleDelete(row) : undefined}
											>
												<MenuRow>
													<Icon icon="trash" size="s" color={canDelete ? 'error' : 'disabled'} />
													<Text color={canDelete ? 'error' : 'disabled'}>
														{formatMessage({id: deleteLabelId})}
													</Text>
												</MenuRow>
											</DropdownHandler.Item>
										</DropdownHandler.Content>
									</DropdownHandler>
								</RowActions>
							</VersionRow>
						);
					})}
					{failedVersion !== null && (
						<Text color="error" data-cy="nova-version-delete-error">
							{formatMessage({id: 'nova.versionHistory.deleteFailed'}, {versionName: nameOf(failedVersion)})}
						</Text>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-version-history-close">
					{formatMessage({id: 'nova.versionHistory.closeButton'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
